import React, { useCallback, useContext, useState } from "react";
import styled from "styled-components";
import { Button, Spinner } from "@blueprintjs/core";

import { deleteDeploymentRepo } from "@api";
import { AppToaster, RowSelectDialogProps } from "@components";
import { OrgContext } from "@providers";

import { Repo } from "@pages/projects/types";

const DialogContainer = styled.div`
  padding: 20px;
  width: 348px;
`;

const Header = styled.div`
  font-size: var(--global-font-size-m);
`;

const Subheader = styled.div`
  font-size: var(--global-font-size-xs);
  color: var(--link-color);
`;

const StyledHr = styled.hr`
  background-color: var(--global-light-gray);
  height: 1px;
  border: none;
  margin-bottom: 20px;
`;

const WarningText = styled.div`
  color: var(--gray1);
  font-size: var(--global-font-size-xs);
  margin-bottom: 8px;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

export const DeleteProjectsDialog: React.FC<RowSelectDialogProps<Repo>> = ({
  selected,
  selectAll,
  closeDialog,
  refresh,
  availableItems,
}) => {
  const { org } = useContext(OrgContext);
  const [isDeleting, setIsDeleting] = useState(false);

  const onDelete = useCallback(
    (_: boolean) => {
      if (org) {
        // TODO: Add repo batch delete endpoint
        let deleteAll;
        if (selectAll && availableItems) {
          deleteAll = Promise.all(
            availableItems.map(
              (repo) => repo.name && deleteDeploymentRepo(org.id, repo.name)
            )
          );
        } else {
          deleteAll = Promise.all(
            selected.map(
              (repo) => repo.name && deleteDeploymentRepo(org.id, repo.name)
            )
          );
        }
        setIsDeleting(true);
        deleteAll
          .catch(() => {
            setIsDeleting(false);
            AppToaster.show({
              message: "Selected projects could not be deleted",
            });
          })
          .then(refresh)
          // Excuse the hack: these 3-second pauses keep delete dialog open
          // and spinner spinning while the refresh actually loads new data
          .then(() => setTimeout(() => setIsDeleting(false), 3100))
          .then(() => setTimeout(() => closeDialog(), 3000));
      }
    },
    [org, refresh, selected, selectAll, closeDialog, availableItems]
  );

  if (selected.length === 0) {
    // This dialog should never be rendered with no repos selected, but just in case
    return <></>;
  }

  const selectedLength = selectAll ? availableItems?.length : selected.length;

  return (
    <DialogContainer>
      <Header>Delete Projects</Header>
      <Subheader></Subheader>
      <StyledHr />
      {isDeleting ? (
        <div style={{ padding: "20px" }}>
          <Spinner />
        </div>
      ) : (
        <>
          <WarningText>
            <b>Warning:</b> Are you sure you want to delete {selectedLength} /{" "}
            {availableItems?.length} projects? This will irreversibly delete all
            data we have about your repository and its prior Semgrep findings.
            In order to prevent Semgrep from running on this repository in the{" "}
            <em>future</em>, you will need to delete the Semgrep CI file from
            your CI configurations folder as well.
          </WarningText>
        </>
      )}
      <Buttons>
        <Button
          intent="primary"
          outlined
          onClick={closeDialog}
          disabled={isDeleting}
          data-testid="delete-projects-cancel"
        >
          Cancel
        </Button>
        <Button
          intent="primary"
          onClick={() => onDelete(!!selectAll)}
          disabled={isDeleting}
          data-testid="delete-projects-submit"
        >
          Delete
        </Button>
      </Buttons>
    </DialogContainer>
  );
};
